import React, { useMemo, useState } from 'react';
import { Button } from '../../atoms/button';
import { InputField } from '../../atoms/input-field';
import { TextAreaField } from '../../atoms/text-area-field';
import S3FilePicker from '../../molecules/filepickers/s3-file-picker/S3FilePicker';
import styles from './ManageSocialSharing.scss';

interface ManageSocialSharingProps {
    title: string;
    description: string;
    imageUrl: string;
    uploadModel: any;
    appealKey: string;
}

const ManageSocialSharing = ({
    title,
    description,
    imageUrl,
    uploadModel,
    appealKey
}: ManageSocialSharingProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccessful, setIsSuccessful] = useState(false);
    const [form, setForm] = useState({
        title,
        description,
        imageUrl,
        imageExt: '', // no initial value needed, only used when image is being updated
    });

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }

    const handleSubmit = () => {
        setIsLoading(true);
        fetch('/Portal/Appeal/Manage/SocialSharing', {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                AppealKey: appealKey,
                OGTitle: form.title,
                OGDescription: form.description,
                OGImageUploadForm: {
                    Value: uploadModel.bucketpath, // we don't want to upload it again if it's the same
                    MetaData: {
                        Extension: form.imageExt,
                    }
                }
            })
        })
            .then((res) => res.json())
            .then((json) => {
                if (json.success) {
                    setIsSuccessful(true);
                }
            })
            .finally(() => setIsLoading(false));
    }

    const isInvalid = !form.title || !form.description || !form.imageUrl
        || (form.title === title && form.description === description && form.imageUrl === imageUrl);

    return (
        <div className={`${styles['c-manage-social']} layout-grouping layout-pinned`}>
            <div className={`${styles['c-manage-social__form']} layout-grouping-children layout-grouping-children--medium`}>
                <InputField
                    id="og-title"
                    name="title"
                    label="Title"
                    value={form.title}
                    onChange={handleFormChange}
                    placeholder="Title"
                    disabled={isLoading}
                />
                <TextAreaField
                    id="og-description"
                    name="description"
                    label="Description"
                    value={form.description}
                    onChange={handleFormChange}
                    placeholder="Description"
                    disabled={isLoading}
                />
                <div>
                    <label className={styles['c-manage-social__preview-label']}>Preview Image</label>
                    <S3FilePicker
                        {...uploadModel}
                        name="Preview image upload"
                        maxsize={2}
                        extensions={['.png', '.jpg', '.jpeg']}
                        editFile={Boolean(form.imageUrl)}
                        onhandlefilechanged={(fileUrl: string, file: File) => {
                            const fileName = file.name;
                            const ext = fileName.substr(fileName.lastIndexOf('.') + 1);

                            setForm((prev) => ({ ...prev, imageUrl: fileUrl, imageExt: ext }));

                            return true;
                        }}
                        identifyImageType={(_: string) => true}
                    />
                    <p className={styles['c-manage-social__preview-subtitle']}>{`Recommended resolution is 1200x630px`}</p>
                </div>
                <div>
                    <Button
                        id="submit"
                        type="submit"
                        appearance="new-primary"
                        disabled={isInvalid}
                        isLoading={isLoading}
                        onClick={handleSubmit}
                    >
                        Save Changes
                    </Button>
                </div>
                {isSuccessful && <p>Changes saved successfully!</p>}
            </div>
            <div className={styles['c-manage-social__preview']}>
                <div className={styles['c-manage-social__preview-empty']}>
                    {form.imageUrl && (
                        <img className={styles['c-manage-social__preview-image']} src={form.imageUrl} />
                    )}
                </div>
                <div className={styles['c-manage-social__preview-details']}>
                    <h1 className={styles['c-manage-social__preview-title']}>{form.title || 'Title'}</h1>
                    <p className={styles['c-manage-social__preview-description']}>{form.description || 'This is a description that appears when you share this page on social media'}</p>
                </div>
            </div>
        </div>
    );
}

interface ManageSocialSharingAppProps {
    ogimageurl: string;
    ogdescription: string;
    ogtitle: string;
    uploadmodel: any;
    returnurl: string;
    appealname: string;
    appealkey: string;
}

const ManageSocialSharingApp = ({
    ogimageurl,
    ogdescription,
    ogtitle,
    uploadmodel,
    returnurl,
    appealname,
    appealkey
}: ManageSocialSharingAppProps) => {
    const uploadModel = JSON.parse(uploadmodel);
    // the serialization from c# returns it in PascalCase, but we want all lowercase
    const parsedUploadModel = Object.keys(uploadModel).reduce((acc, curr) => ({ ...acc, [curr.toLowerCase()]: uploadModel[curr] }), {});

    return (
        <>
            <div className={styles['c-back-bar']}>
                <a className={styles['c-back-bar__link']} href={returnurl}>
                    <i className="fa fa-arrow-left" aria-hidden="true"></i>
                    {'  '}
                    Return to Editor
                </a>
            </div>
            <h1 className={styles['c-manage-social-heading']}>Manage Social Preview for {appealname}</h1>
            <ManageSocialSharing
                title={ogtitle ?? ''}
                description={ogdescription ?? ''}
                imageUrl={ogimageurl}
                uploadModel={parsedUploadModel}
                appealKey={appealkey}
            />
        </>
    );
}

export default ManageSocialSharingApp;